// Price my floor — which products the public website may price (Jacky, 9 Oct 2026).
//
// "I want an independent page on AIW to control the products available for Price my floor
// indication ... if not listed ... it should say 'Available upon request'." A listed product shows its
// price on the website ("from $45/m² + GST") and can be chosen in Price my floor; every other product
// reads "Available upon request".
//
// Layout (review, 9 Oct): ONE table with a category header row per group, so the columns line up down
// the whole page. Units live in the column headers, not on every row.
//
// Redesign (Jacky, 25 Oct 2026: "I need filter and sort by product category and supplier and quick list to
// show how many product in each category listed with price, i need to able to expand on any product to reveal
// the calculation on cost … format the table with a cell line of some sort, its too busy at the moment"):
//  - a per-category summary on top (click a category to filter);
//  - category, supplier and status filters; click a heading to sort (category grouping is the default sort);
//  - cell gridlines, one status per row, the supplier in its own column;
//  - click a product to see how its cost is worked out — the shared @aiw/pricing breakdown, the same one EmmA
//    reads (`price_my_floor_cost`), including when the page's figure is a raw-catalogue fallback;
//  - "Brief EmmA" opens her composer: his instruction is the approval for these commands (ADR-027).
//
// Every change goes through set_price_my_floor / set_product_sell_price / set_installation_price (ADR-003).
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { FLOATING_INSTALLS, priceForMargin } from "@artemis-group/aiw-contracts";
import type { MessageId } from "@aiw/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, ChevronsUpDown, MessageSquareText, Search } from "lucide-react";
import { Button, cn } from "@aiw/ui";
import { Page, Panel } from "../shell/Page";
import { usePmfInclusions, usePriceMyFloor, usePriceMyFloorCost, type PriceMyFloorRow } from "../hooks/useReads";
import { useProposer, OUTCOME_TONE } from "../hooks/slice-b";
import { askEmma } from "../lib/communication-office";
import { useT } from "../lib/i18n";
import { day } from "../lib/format";

type T = ReturnType<typeof useT>["t"];
const money = (n: number) => "$" + (Number.isInteger(n) ? String(n) : n.toFixed(2));
/** One figure when every colour costs the same, otherwise cheapest–dearest colour. */
const costText = (from: number, to: number | null) => (to != null && to !== from ? `${money(from)}–${money(to).slice(1)}` : money(from));
/** The estimator's cost behind the price (25 Oct 2026, worked out on the server): supplier cost + installation + the
 *  included underlay/scotia at the manufacturer's prices, no waste. Null while any part is unknown — a GM on half the
 *  cost would flatter the price. Engineered timber: the first method's (Floating for a plank). */
const totalCostOf = (p: PriceMyFloorRow) => p.estimateCostM2;
/** The supplied & installed price the GM is on: the list price, or for engineered timber supply + the first method. */
const priceOf = (p: PriceMyFloorRow) => (p.installBasis === "supply" ? p.methods[0]?.price ?? null : p.sellExGst);
/** Whole dollars, rounded up (Jacky: "cell up to whole $"). */
const wholeUp = (raw: string) => { const n = Number(raw.replace(/[$,\s]/g, "")); return Number.isFinite(n) && n > 0 ? Math.ceil(n) : null; };
const pctOf = (raw: string) => { const n = Number(raw.replace(/[%\s]/g, "")); return Number.isFinite(n) && n > -100 && n < 100 ? n / 100 : null; };
/** Gross margin of the list price over the total cost. */
const gmOf = (p: PriceMyFloorRow) => { const c = totalCostOf(p), price = priceOf(p); return price && c != null ? (price - c) / price : null; };
/** The refusal, said in a few words for the row; the full sentence stays in the tooltip. */
const shortRefusal = (r: string, t: T) => (/no sell price|no supplied & installed price/.test(r) ? t("pmf.refusal.noPrice") : /not priced per m²/.test(r) ? t("pmf.refusal.notM2") : /at or below its cost/.test(r) ? t("pmf.refusal.atCost") : r);

/** Engineered timber's methods, in a word (Jacky: "Floating | Direct Glued"). */
const methodLabel = (id: string, t: T) => t(id === "engineered" ? "pmf.method.floating" : id === "engineeredGlue" ? "pmf.method.glued" : id === "herringbone" ? "pmf.method.herringbone" : id === "chevron" ? "pmf.method.chevron" : "pmf.method.standard");

type Status = "listed" | "ready" | "blocked";
const statusOf = (p: PriceMyFloorRow): Status => (p.listed ? "listed" : p.refusal ? "blocked" : "ready");
const isLoss = (p: PriceMyFloorRow) => { const g = gmOf(p); return g != null && g <= 0; };

const SEGMENTS = { residential: "pmf.segment.residential", commercial: "pmf.segment.commercial", all: "pmf.segment.all" } as const satisfies Record<string, MessageId>;
type SegmentKey = keyof typeof SEGMENTS;
type StatusFilter = "" | Status | "loss" | "unresolved";
type SortKey = "category" | "name" | "supplier" | "cost" | "install" | "included" | "total" | "price" | "gm" | "website";
interface Filters { segment: SegmentKey; category: string; supplier: string; status: StatusFilter; needle: string; sort: SortKey; dir: 1 | -1 }
const DEFAULT_FILTERS: Filters = { segment: "residential", category: "", supplier: "", status: "", needle: "", sort: "category", dir: 1 };
const STORE = "aiw.priceMyFloor.filters";
/** Filters and sort are a per-device convenience (like the text size): remembered in this browser only. */
function useFilters() {
  const [f, setF] = useState<Filters>(() => { try { return { ...DEFAULT_FILTERS, ...JSON.parse(localStorage.getItem(STORE) ?? "{}"), needle: "" }; } catch { return DEFAULT_FILTERS; } });
  useEffect(() => { try { localStorage.setItem(STORE, JSON.stringify({ ...f, needle: "" })); } catch { /* storage unavailable */ } }, [f]);
  return [f, (patch: Partial<Filters>) => setF((x) => ({ ...x, ...patch }))] as const;
}

const seg = (on: boolean) => cn("rounded-md px-3 py-1 text-sm transition-colors", on ? "bg-card font-medium text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground");
/** Cell gridlines (25 Oct 2026): every cell carries its own hairline, so columns read down the page. */
const CELL = "border border-border px-2.5 py-2";
/** Header cells stick to the top of the table's scroll area (Jacky, 26 Oct 2026: "header is unseen when you scroll down"). */
const TH = cn(CELL, "sticky top-0 z-10 bg-card font-mono text-micro font-medium uppercase leading-tight text-muted-foreground align-bottom");
const SELECT = "h-9 rounded-md border bg-card px-2 text-sm";

/** Wide enough for a separate "Website shows" column? Below it the status sits under the product name,
 *  so the Listed switch — the page's one control — never falls off the right edge (1024 px iPad). */
function useWide(query = "(min-width: 1280px)") {
  const [wide, setWide] = useState(() => typeof window === "undefined" || window.matchMedia(query).matches);
  useEffect(() => { const m = window.matchMedia(query); const on = () => setWide(m.matches); m.addEventListener("change", on); return () => m.removeEventListener("change", on); }, [query]);
  return wide;
}

export function PriceMyFloor() {
  const { t } = useT();
  const wide = useWide();
  const read = usePriceMyFloor();
  const qc = useQueryClient();
  const { send, outcomes, busy } = useProposer();
  const [f, set] = useFilters();
  const [open, setOpen] = useState<string | null>(null);

  const inSegment = useMemo(() => (read.data?.products ?? []).filter((p) => f.segment === "all" || (f.segment === "residential" ? p.residential : p.commercial)), [read.data, f.segment]);
  const categories = useMemo(() => [...new Set(inSegment.map((p) => p.subType))].sort((a, b) => a.localeCompare(b)), [inSegment]);
  const suppliers = useMemo(() => [...new Set(inSegment.map((p) => p.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [inSegment]);
  // The quick list (Jacky: "how many product in each category listed with price"): per category in this segment.
  const summary = useMemo(() => categories.map((c) => {
    const items = inSegment.filter((p) => p.subType === c);
    return { category: c, products: items.length, listed: items.filter((p) => p.listed && p.sellExGst != null).length, ready: items.filter((p) => statusOf(p) === "ready").length,
      blocked: items.filter((p) => statusOf(p) === "blocked").length, loss: items.filter(isLoss).length, unresolved: items.filter((p) => !p.costResolved).length };
  }), [categories, inSegment]);
  const totals = useMemo(() => summary.reduce((a, r) => ({ products: a.products + r.products, listed: a.listed + r.listed, ready: a.ready + r.ready, blocked: a.blocked + r.blocked, loss: a.loss + r.loss, unresolved: a.unresolved + r.unresolved }),
    { products: 0, listed: 0, ready: 0, blocked: 0, loss: 0, unresolved: 0 }), [summary]);

  const rows = useMemo(() => {
    const q = f.needle.trim().toLowerCase();
    const kept = inSegment.filter((p) => (!f.category || p.subType === f.category) && (!f.supplier || p.brand === f.supplier)
      && (!f.status || (f.status === "loss" ? isLoss(p) : f.status === "unresolved" ? !p.costResolved : statusOf(p) === f.status))
      && (!q || `${p.customerName} ${p.name} ${p.brand} ${p.subType}`.toLowerCase().includes(q)));
    const key: Record<SortKey, (p: PriceMyFloorRow) => string | number | null> = {
      category: (p) => p.subType, name: (p) => p.customerName.toLowerCase(), supplier: (p) => p.brand.toLowerCase(), cost: (p) => p.supplierCostTo,
      install: (p) => p.installCostM2, included: (p) => p.includedCostM2, total: totalCostOf, price: priceOf, gm: gmOf, website: (p) => ["listed", "ready", "blocked"].indexOf(statusOf(p)),
    };
    const by = key[f.sort];
    // Unknown figures sort last whichever way the column runs, so a sort never buries the products that have one.
    const cmp = (a: PriceMyFloorRow, b: PriceMyFloorRow) => { const x = by(a), y = by(b); if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1; return (x < y ? -1 : x > y ? 1 : 0) * f.dir; };
    return [...kept].sort((a, b) => cmp(a, b) || (a.sellExGst ?? 1e9) - (b.sellExGst ?? 1e9) || a.customerName.localeCompare(b.customerName));
  }, [inSegment, f]);
  const grouped = f.sort === "category";
  const groups = useMemo(() => {
    if (!grouped) return [["", rows] as [string, PriceMyFloorRow[]]];
    const m = new Map<string, PriceMyFloorRow[]>();
    for (const r of rows) m.set(r.subType, [...(m.get(r.subType) ?? []), r]);
    return [...m.entries()];
  }, [rows, grouped]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["price-my-floor"] });
  const setPrice = async (p: PriceMyFloorRow, price: number) => {
    const o = await send(`price:${p.id}`, "set_product_sell_price", { product_id: p.id, sell_price: price, expected_sell_price: p.sellExGst, reason: null }, t("pmf.done.price", { price: money(price) }));
    if (o.tone === "ok") await refresh();
  };
  /** Timber: one method's own supplied & installed price (26 Oct 2026, "one GM on total"). */
  const setMethodPrice = async (p: PriceMyFloorRow, methodId: string, price: number, expected: number | null) => {
    const o = await send(`price:${p.id}`, "set_price_my_floor_method_prices", { items: [{ product_id: p.id, install_id: methodId, sell_m2: price, expected_sell_m2: expected }], reason: null }, t("pmf.done.price", { price: money(price) }));
    if (o.tone === "ok") await refresh();
  };

  const change = async (key: string, ids: string[], listed: boolean) => {
    const o = await send(key, "set_price_my_floor", { product_ids: ids, listed, reason: null }, listed ? t("pmf.done.listed") : t("pmf.done.unlisted"));
    if (o.tone === "ok") await refresh();
  };
  const sortBy = (k: SortKey) => set({ sort: k, dir: f.sort === k ? (f.dir === 1 ? -1 : 1) : 1 });
  const cols = wide ? 10 : 9;

  return (
    <Page kicker={t("pmf.kicker")} title={t("pmf.title")} lede={t("pmf.lede")}>
      {summary.length > 0 && (
        <Panel className="mb-4 overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead className="text-left"><tr>
                <th className={TH}>{t("pmf.sum.category")}</th>
                {(["products", "listed", "ready", "blocked", "loss", "unresolved"] as const).map((k) => <th key={k} className={cn(TH, "text-right")}>{t(`pmf.sum.${k}` as MessageId)}</th>)}
              </tr></thead>
              <tbody>
                {[...summary, { category: "", ...totals }].map((r) => (
                  <tr key={r.category || "all"} onClick={() => set({ category: r.category })}
                    className={cn("cursor-pointer hover:bg-muted/40", f.category === r.category && "bg-primary/5", !r.category && "font-medium")}>
                    <td className={CELL}>{r.category || t("pmf.sum.all")}</td>
                    <td className={cn(CELL, "num text-right")}>{r.products}</td>
                    <td className={cn(CELL, "num text-right font-medium text-success")}>{r.listed}</td>
                    <td className={cn(CELL, "num text-right")}>{r.ready}</td>
                    <td className={cn(CELL, "num text-right text-muted-foreground")}>{r.blocked}</td>
                    <td className={cn(CELL, "num text-right", r.loss > 0 && "text-warning")}>{r.loss}</td>
                    <td className={cn(CELL, "num text-right", r.unresolved > 0 && "text-warning")}>{r.unresolved}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <InclusionsPanel products={read.data?.products ?? []} t={t} send={send} busy={!!busy["inclusions"]} outcome={outcomes["inclusions"]} onSaved={refresh} />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex h-9 min-w-[220px] flex-1 items-center gap-2 rounded-md border bg-card px-2.5 focus-within:border-primary">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input value={f.needle} onChange={(e) => set({ needle: e.target.value })} placeholder={t("pmf.search")} className="w-full bg-transparent text-sm outline-none" aria-label={t("pmf.search")} />
        </label>
        <select className={SELECT} value={f.category} onChange={(e) => set({ category: e.target.value })} aria-label={t("pmf.sum.category")}>
          <option value="">{t("pmf.filter.category")}</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={SELECT} value={f.supplier} onChange={(e) => set({ supplier: e.target.value })} aria-label={t("pmf.col.supplier")}>
          <option value="">{t("pmf.filter.supplier")}</option>
          {suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={SELECT} value={f.status} onChange={(e) => set({ status: e.target.value as StatusFilter })} aria-label={t("pmf.col.website")}>
          <option value="">{t("pmf.filter.status")}</option>
          {(["listed", "ready", "blocked", "loss", "unresolved"] as const).map((s) => <option key={s} value={s}>{t(`pmf.filter.${s}` as MessageId)}</option>)}
        </select>
        <div className="flex rounded-lg bg-muted p-0.5" role="tablist" aria-label={t("pmf.segment.all")}>
          {(Object.keys(SEGMENTS) as SegmentKey[]).map((s) => <button key={s} role="tab" aria-selected={f.segment === s} className={seg(f.segment === s)} onClick={() => set({ segment: s })}>{t(SEGMENTS[s])}</button>)}
        </div>
        <Button size="sm" variant="outline" onClick={() => askEmma(t("pmf.briefEmma.prompt"))}><MessageSquareText className="mr-1 h-4 w-4" aria-hidden />{t("pmf.briefEmma")}</Button>
      </div>

      <div className="mb-3 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
        <span><b className="num font-semibold">{totals.listed}</b> <span className="text-muted-foreground">{t("pmf.tally.listed")}</span></span>
        <span><b className="num font-semibold">{totals.ready}</b> <span className="text-muted-foreground">{t("pmf.tally.ready")}</span></span>
        {totals.blocked > 0 && <span><b className="num font-semibold">{totals.blocked}</b> <span className="text-muted-foreground">{t("pmf.tally.blocked")}</span></span>}
        {totals.loss > 0 && <span><b className="num font-semibold text-warning">{totals.loss}</b> <span className="text-muted-foreground">{t("pmf.tally.loss")}</span></span>}
        {totals.unresolved > 0 && <span><b className="num font-semibold text-warning">{totals.unresolved}</b> <span className="text-muted-foreground">{t("pmf.tally.unresolved")}</span></span>}
        {!grouped && <button type="button" className="text-primary underline-offset-4 hover:underline" onClick={() => set({ sort: "category", dir: 1 })}>{t("pmf.sort.group")}</button>}
        <span className="ml-auto font-mono text-micro text-muted-foreground">{t("pmf.units")}</span>
      </div>

      {read.isLoading && <p className="text-sm text-muted-foreground">{t("pmf.loading")}</p>}
      {read.error && <p className="text-sm text-warning">{(read.error as Error).message}</p>}

      {rows.length > 0 && (
        <Panel className="overflow-hidden p-0">
          {/* The table scrolls inside the panel (both ways), so its header stays in view while the rows move. */}
          <div className="max-h-[calc(100vh-11rem)] overflow-auto">
            <table className={cn("w-full table-fixed border-collapse text-sm", wide ? "min-w-[1180px]" : "min-w-[900px]")}>
              <colgroup>
                {/* Inline widths, not utility classes: a fixed-layout table must get these exact numbers or the
                    product column is what gets squeezed. */}
                <col />
                {(wide ? [128, 92, 80, 84, 96, 184, 64, 168, 64] : [104, 84, 72, 80, 88, 172, 60, 60]).map((w, i) => <col key={i} style={{ width: w }} />)}
              </colgroup>
              <thead className="text-left">
                <tr>
                  <SortHead k="name" f={f} onSort={sortBy} label={t("pmf.col.product")} t={t} />
                  <SortHead k="supplier" f={f} onSort={sortBy} label={t("pmf.col.supplier")} t={t} />
                  <SortHead k="cost" f={f} onSort={sortBy} label={t("pmf.col.supplierCost")} t={t} right />
                  <SortHead k="install" f={f} onSort={sortBy} label={t("pmf.col.install")} t={t} right />
                  <SortHead k="included" f={f} onSort={sortBy} label={t("pmf.col.included")} t={t} right />
                  <SortHead k="total" f={f} onSort={sortBy} label={t("pmf.col.total")} t={t} right />
                  <SortHead k="price" f={f} onSort={sortBy} label={t("pmf.col.price")} t={t} right strong />
                  <SortHead k="gm" f={f} onSort={sortBy} label={t("pmf.col.gm")} t={t} right />
                  {wide && <SortHead k="website" f={f} onSort={sortBy} label={t("pmf.col.website")} t={t} />}
                  <th className={cn(TH, "text-right")}>{t("pmf.col.listed")}</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(([subType, items]) => {
                  const eligible = items.filter((p) => statusOf(p) === "ready").map((p) => p.id);
                  const groupKey = `group:${subType}`;
                  const go = outcomes[groupKey];
                  return (
                    <Fragment key={subType || "all"}>
                      {grouped && (
                        <tr className="bg-muted/40">
                          <th colSpan={cols} className={cn(CELL, "py-1.5 text-left font-normal")}>
                            <div className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1">
                              <span className="font-medium">{subType}</span>
                              <span className="font-mono text-micro text-muted-foreground">{t("pmf.cat.listedOf", { listed: items.filter((p) => p.listed).length, total: items.length })}</span>
                              {items.some((p) => p.installBasis === "supply")
                                ? <span className="text-micro text-muted-foreground">{t("pmf.cat.timberMethods")}</span>
                                : <InstallIncluded items={items} t={t} />}
                              <IncludesLine items={items} t={t} />
                              {go && <span className={cn("text-micro", OUTCOME_TONE[go.tone])}>{go.text}</span>}
                              {eligible.length > 1 && (
                                <Button size="sm" variant="ghost" className="ml-auto" disabled={busy[groupKey]} onClick={() => change(groupKey, eligible, true)}>
                                  {t("pmf.cat.listReady", { count: eligible.length })}
                                </Button>
                              )}
                            </div>
                          </th>
                        </tr>
                      )}
                      {items.map((p) => (
                        <Fragment key={p.id}>
                          <ProductRow p={p} wide={wide} t={t} open={open === p.id} onOpen={() => setOpen(open === p.id ? null : p.id)}
                            outcome={outcomes[p.id] ?? outcomes[`price:${p.id}`]} busy={!!busy[p.id]} saving={!!busy[`price:${p.id}`]}
                            onToggle={() => change(p.id, [p.id], !p.listed)} onPrice={(price) => setPrice(p, price)}
                            onMethodPrice={(id, price, expected) => setMethodPrice(p, id, price, expected)} />
                          {open === p.id && <tr><td colSpan={cols} className={cn(CELL, "bg-card px-4 py-3")}><CostBreakdown p={p} t={t} /></td></tr>}
                        </Fragment>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      {!read.isLoading && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("pmf.none")}</p>}
      <p className="mt-3 text-micro text-muted-foreground">{t("pmf.foot")}</p>
    </Page>
  );
}

function SortHead({ k, f, onSort, label, t, right, strong }: { k: SortKey; f: Filters; onSort: (k: SortKey) => void; label: string; t: T; right?: boolean; strong?: boolean }) {
  const on = f.sort === k;
  const Icon = on ? ChevronDown : ChevronsUpDown;
  return (
    <th className={cn(TH, right && "text-right", strong && "text-foreground")} aria-sort={on ? (f.dir === 1 ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(k)} title={t("pmf.col.sortBy", { column: label })} className={cn("inline-flex items-end gap-1 text-left uppercase hover:text-foreground", right && "justify-end text-right", on && "text-foreground")}>
        {label}<Icon className={cn("h-3 w-3", on && f.dir === -1 && "rotate-180", !on && "opacity-50")} aria-hidden />
      </button>
    </th>
  );
}

/** A figure that edits in place. Click to edit; Enter or leaving the field saves; Escape restores. Nothing is
 *  sent while typing — only a committed value, and only if it changed. */
function EditCell({ display, initial, onSave, preview, disabled, title, label, align = "right" }: {
  display: React.ReactNode; initial: string; onSave: (raw: string) => void; preview?: (raw: string) => string | null;
  disabled?: boolean; title?: string; label: string; align?: "right" | "left";
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) { ref.current?.focus(); ref.current?.select(); } }, [editing]);
  if (!editing) return (
    <button type="button" disabled={disabled} title={title} aria-label={label}
      onClick={(e) => { e.stopPropagation(); setDraft(initial); setEditing(true); }}
      className={cn("num -mx-1.5 rounded px-1.5 py-0.5 decoration-dotted underline-offset-4 hover:bg-muted hover:underline disabled:cursor-default disabled:hover:bg-transparent disabled:hover:no-underline", align === "right" && "text-right")}>
      {display}
    </button>
  );
  const commit = () => { setEditing(false); if (draft.trim() !== initial.trim()) onSave(draft); };
  const hint = preview?.(draft);
  return (
    <span className="relative inline-flex flex-col items-end" onClick={(e) => e.stopPropagation()}>
      <input ref={ref} value={draft} inputMode="decimal" aria-label={label}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        className="num h-7 w-[76px] rounded border border-primary bg-card px-1.5 text-right text-sm outline-none" />
      {hint && <span className="absolute top-full z-10 mt-0.5 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-micro text-background">{hint}</span>}
    </span>
  );
}

/** The category's standard installation, stated once on its header row: what the list price includes
 *  and what it costs per m². A category with more than one method names each. */
function InstallIncluded({ items, t }: { items: PriceMyFloorRow[]; t: T }) {
  const methods = new Map<string, { name: string; cost: number | null }>();
  for (const p of items) if (p.installId && p.installName) methods.set(p.installId, { name: p.installName, cost: p.installCostM2 });
  if (methods.size === 0) return <span className="text-micro text-warning">{t("pmf.cat.noInstall")}</span>;
  return (
    <span className="text-micro text-muted-foreground">
      {t("pmf.cat.included")}{" "}
      {[...methods.values()].map((m, i) => (
        <span key={m.name}>{i > 0 && " · "}<span className="text-foreground">{m.name}</span> <span className="num">{m.cost != null ? t("pmf.cat.perM2", { amount: money(m.cost) }) : t("pmf.cat.costUnknown")}</span></span>
      ))}
    </span>
  );
}

function ProductRow({ p, wide, t, open, onOpen, outcome, busy, saving, onToggle, onPrice, onMethodPrice }: {
  p: PriceMyFloorRow; wide: boolean; t: T; open: boolean; onOpen: () => void; outcome?: { tone: "ok" | "wait" | "no"; text: string };
  busy: boolean; saving: boolean; onToggle: () => void; onPrice: (price: number) => void; onMethodPrice: (methodId: string, price: number, expected: number | null) => void;
}) {
  const total = totalCostOf(p), gm = gmOf(p);
  const timber = p.installBasis === "supply";
  const blocked = statusOf(p) === "blocked";
  const status = (
    <>
      {p.listed ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2 py-0.5 text-micro text-success">
          <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
          {t("pmf.status.from", { price: p.sellExGst != null ? money(p.sellExGst) : "—" })}
        </span>
      ) : blocked ? (
        <span className="inline-flex items-center rounded-full bg-warning/15 px-2 py-0.5 text-micro text-warning" title={p.refusal!}>{shortRefusal(p.refusal!, t)}</span>
      ) : (
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-micro text-muted-foreground">{t("pmf.status.request")}</span>
      )}
      {outcome && outcome.tone !== "ok" && <div className={cn("mt-1 text-micro", OUTCOME_TONE[outcome.tone])}>{outcome.text}</div>}
    </>
  );
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <tr className={cn("cursor-pointer align-middle hover:bg-muted/30", open && "bg-muted/30")} onClick={onOpen} aria-expanded={open}>
      <td className={CELL}>
        <div className="flex items-center gap-1.5">
          <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate font-medium" title={p.customerName}>{p.customerName}</span>
        </div>
        {!p.website && <div className="pl-5 text-micro text-muted-foreground">{t("pmf.status.notOnWebsite")}</div>}
        {!wide && <div className="mt-1 pl-5">{status}</div>}
      </td>
      <td className={cn(CELL, "truncate text-muted-foreground")} title={p.brand}>{p.brand}</td>
      <td className={cn(CELL, "num text-right text-muted-foreground", !p.costResolved && "text-warning")} title={!p.costResolved ? t("pmf.tip.unresolved") : undefined}>
        {p.supplierCostFrom != null ? <>{!p.costResolved && "≈ "}{costText(p.supplierCostFrom, p.supplierCostTo)}</> : "—"}
      </td>
      <td className={cn(CELL, "num text-right text-muted-foreground")} title={p.installName ?? t("pmf.tip.noInstall")}>
        {timber ? <Lines p={p} cell={(m) => (m.installCostM2 != null ? money(m.installCostM2) : "—")} /> : p.installCostM2 != null ? money(p.installCostM2) : "—"}
      </td>
      <td className={cn(CELL, "num text-right", p.includedCostM2 == null ? "text-warning" : "text-muted-foreground")}
        title={p.includedCostM2 == null ? t("pmf.tip.includedMissing", { what: p.includedMissing.map((m) => t(`pmf.inc.m.${m}` as MessageId)).join(", ") }) : p.included.join(" · ")}>
        {timber ? <Lines p={p} cell={(m) => (m.includedCostM2 == null ? t("pmf.inc.missing") : money(m.includedCostM2))} />
          : p.includedCostM2 == null ? t("pmf.inc.missing") : p.included.length > 1 ? money(p.includedCostM2) : "—"}
      </td>
      <td className={cn(CELL, "num text-right")}>{timber ? <Lines p={p} cell={(m) => (m.costM2 != null ? money(m.costM2) : "—")} /> : total != null ? money(total) : "—"}</td>
      <td className={cn(CELL, "text-right font-medium")}>
        {timber ? (
          <Lines p={p} cell={(m) => (
            // Each method's own supplied & installed price (26 Oct 2026, "B: one GM on total") — typed directly.
            <span className="flex w-full items-center justify-between gap-2">
              <span className="truncate font-normal text-micro text-muted-foreground">{methodLabel(m.id, t)}</span>
              <EditCell label={t("pmf.edit.methodPrice", { name: p.customerName, method: methodLabel(m.id, t) })} disabled={saving} title={t("pmf.tip.methodPrice")}
                display={m.price != null ? money(m.price) : <span className="font-normal text-warning">{t("pmf.set")}</span>} initial={m.price != null ? String(m.price) : ""}
                onSave={(raw) => { const v = wholeUp(raw); if (v != null) onMethodPrice(m.id, v, m.price); }}
                preview={(raw) => { const v = wholeUp(raw); return v == null ? null : m.costM2 != null ? `→ ${money(v)} · ${Math.round(((v - m.costM2) / v) * 100)}% GM` : `→ ${money(v)}`; }} />
            </span>
          )} />
        ) : (
          <EditCell label={t("pmf.edit.price", { name: p.customerName })} disabled={saving}
            title={p.sellExGst != null ? t("pmf.tip.price", { inc: money(Math.round(p.sellExGst * 110) / 100) }) : t("pmf.tip.setPrice")}
            display={p.sellExGst != null ? money(p.sellExGst) : <span className="font-normal text-muted-foreground">{t("pmf.set")}</span>} initial={p.sellExGst != null ? String(p.sellExGst) : ""}
            onSave={(raw) => { const v = wholeUp(raw); if (v != null) onPrice(v); }}
            preview={(raw) => { const v = wholeUp(raw); return v == null ? null : total != null ? `→ ${money(v)} · ${Math.round(((v - total) / v) * 100)}% GM` : `→ ${money(v)}`; }} />
        )}
      </td>
      <td className={cn(CELL, "text-right", gm == null ? "text-muted-foreground" : gm <= 0 ? "font-medium text-warning" : "")}>
        {timber ? (
          <Lines p={p} cell={(m) => (
            <span className={cn(m.gmPct != null && m.gmPct <= 0 && "text-warning")}>
              <EditCell label={t("pmf.edit.methodGm", { name: p.customerName, method: methodLabel(m.id, t) })} disabled={saving || m.costM2 == null}
                title={m.costM2 == null ? (m.missing.join(", ") || t("pmf.tip.gmNeedsCost")) : t("pmf.tip.gm")}
                display={m.gmPct != null ? `${m.gmPct}%` : "—"} initial={m.gmPct != null ? String(m.gmPct) : ""}
                onSave={(raw) => { const g = pctOf(raw); const v = g != null ? priceForMargin(m.costM2, g) : null; if (v != null) onMethodPrice(m.id, v, m.price); }}
                preview={(raw) => { const g = pctOf(raw); const v = g != null ? priceForMargin(m.costM2, g) : null; return v != null ? `→ ${money(v)}` : null; }} />
            </span>
          )} />
        ) : (
          <EditCell label={t("pmf.edit.gm", { name: p.customerName })} disabled={saving || total == null}
            title={total == null ? t("pmf.tip.gmNeedsCost") : gm != null && gm <= 0 ? t("pmf.tip.gmLoss") : t("pmf.tip.gm")}
            display={gm != null ? `${Math.round(gm * 100)}%` : "—"} initial={gm != null ? String(Math.round(gm * 100)) : ""}
            onSave={(raw) => { const m = pctOf(raw); const v = m != null ? priceForMargin(total, m) : null; if (v != null) onPrice(v); }}
            preview={(raw) => { const m = pctOf(raw); const v = m != null ? priceForMargin(total, m) : null; return v != null ? `→ ${t("pmf.cat.perM2", { amount: money(v) })}` : null; }} />
        )}
      </td>
      {wide && <td className={CELL}>{status}</td>}
      <td className={cn(CELL, "text-right")}>
        <button
          type="button"
          role="switch"
          aria-checked={p.listed}
          aria-label={p.listed ? t("pmf.switch.unlist", { name: p.customerName }) : t("pmf.switch.list", { name: p.customerName })}
          disabled={busy || blocked}
          title={blocked ? p.refusal! : p.listed ? t("pmf.tip.listed") : t("pmf.tip.list")}
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className={cn(
            "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40",
            p.listed ? "bg-success" : "bg-muted-foreground/30",
          )}>
          <span className={cn("inline-block h-4 w-4 rounded-full bg-white shadow transition-transform", p.listed ? "translate-x-[18px]" : "translate-x-0.5")} />
        </button>
      </td>
    </tr>
  );
}

/** One line per installation method, each the same height, so a timber row reads straight across every column
 *  (Floating on the first line, Direct glued on the second). */
function Lines({ p, cell }: { p: PriceMyFloorRow; cell: (m: PriceMyFloorRow["methods"][number]) => React.ReactNode }) {
  return <div className="flex flex-col">{p.methods.map((m) => <div key={m.id} className="num flex h-7 items-center justify-end overflow-hidden whitespace-nowrap">{cell(m)}</div>)}</div>;
}

/** What the price includes (Jacky, 25 Oct 2026): installation, underlay on floating floors, scotia on hard floors — trims
 *  and moisture barrier are optional. Stated once per category. */
function IncludesLine({ items, t }: { items: PriceMyFloorRow[]; t: T }) {
  const included = [...new Set(items.flatMap((p) => p.included))];
  if (included.length <= 1) return null;
  return (
    <span className="w-full text-micro text-muted-foreground">
      {t("pmf.inc.includes")} <span className="text-foreground">{included.map((x) => t(`pmf.inc.item.${x.toLowerCase()}` as MessageId)).join(" · ")}</span>
      {" — "}{t("pmf.inc.optional")}
    </span>
  );
}

/** The included accessories by manufacturer (Jacky, 25 Oct 2026: "every manufacturer has different underlay and scotia
 *  prices"): one row per manufacturer and hard-floor category on this page, underlay $/m² (floating floors) and scotia
 *  $/LM, edited in place. Saves through set_price_my_floor_inclusions — the same command EmmA uses. */
function InclusionsPanel({ products, t, send, busy, outcome, onSaved }: {
  products: PriceMyFloorRow[]; t: T; busy: boolean; outcome?: { tone: "ok" | "wait" | "no"; text: string }; onSaved: () => void;
  send: (key: string, command: string, args: Record<string, unknown>, done?: string) => Promise<{ tone: "ok" | "wait" | "no"; text: string }>;
}) {
  const q = usePmfInclusions();
  const [open, setOpen] = useState(false);
  const pairs = useMemo(() => {
    const m = new Map<string, { supplier: string; category: string; floating: boolean; products: number }>();
    for (const p of products) if (p.included.length > 1 && p.supplierName) {
      const k = `${p.supplierName}|${p.subType}`, cur = m.get(k);
      m.set(k, { supplier: p.supplierName, category: p.subType, floating: (cur?.floating ?? false) || p.included.includes("Underlay") || p.methods.some((x) => FLOATING_INSTALLS.includes(x.id)), products: (cur?.products ?? 0) + 1 });
    }
    return [...m.values()].sort((a, b) => a.supplier.localeCompare(b.supplier) || a.category.localeCompare(b.category));
  }, [products]);
  const body = q.data?.body;
  const rowOf = (supplier: string, category: string) => body?.rows.find((r) => r.supplier.trim().toLowerCase() === supplier.trim().toLowerCase() && r.category === category);
  const missing = pairs.filter((x) => { const r = rowOf(x.supplier, x.category); return !r || r.scotia_lm == null || (x.floating && r.underlay_m2 == null); }).length;
  const save = async (supplier: string, category: string, patch: { underlay_m2?: number | null; scotia_lm?: number | null }) => {
    const cur = rowOf(supplier, category);
    const row = { supplier, category, underlay_m2: cur?.underlay_m2 ?? null, scotia_lm: cur?.scotia_lm ?? null, note: cur?.note ?? "", ...patch };
    const o = await send("inclusions", "set_price_my_floor_inclusions", { rows: [row], scotia_lm_per_m2: null, reason: null }, t("pmf.inc.saved"));
    if (o.tone === "ok") { await q.refetch(); onSaved(); }
  };
  const amount = (raw: string) => { const v = raw.trim() === "" ? null : Number(raw.replace(/[$,\s]/g, "")); return v == null || (Number.isFinite(v) && v >= 0) ? (v == null ? null : Math.round(v * 100) / 100) : undefined; };
  if (!pairs.length) return null;
  return (
    <Panel className="mb-4 overflow-hidden p-0">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm" aria-expanded={open}>
        {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
        <span className="font-medium">{t("pmf.inc.title")}</span>
        <span className="text-micro text-muted-foreground">{t("pmf.inc.scotiaRule", { lm: body?.scotia_lm_per_m2 ?? 1 })}</span>
        {missing > 0 && <span className="ml-auto rounded-full bg-warning/15 px-2 py-0.5 text-micro text-warning">{t("pmf.inc.missingCount", { count: missing })}</span>}
        {outcome && outcome.tone !== "ok" && <span className={cn("text-micro", OUTCOME_TONE[outcome.tone])}>{outcome.text}</span>}
      </button>
      {open && (
        <div className="overflow-x-auto border-t">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead className="text-left"><tr>
              <th className={TH}>{t("pmf.inc.manufacturer")}</th><th className={TH}>{t("pmf.sum.category")}</th>
              <th className={cn(TH, "text-right")}>{t("pmf.inc.underlay")}</th><th className={cn(TH, "text-right")}>{t("pmf.inc.scotia")}</th><th className={TH}>{t("pmf.inc.note")}</th>
            </tr></thead>
            <tbody>
              {pairs.map((x) => {
                const r = rowOf(x.supplier, x.category);
                return (
                  <tr key={`${x.supplier}|${x.category}`}>
                    <td className={CELL}>{x.supplier}</td>
                    <td className={cn(CELL, "text-muted-foreground")}>{x.category} · {x.products}</td>
                    <td className={cn(CELL, "text-right")}>
                      {x.floating
                        ? <EditCell label={t("pmf.inc.underlayFor", { name: `${x.supplier} ${x.category}` })} disabled={busy} title={t("pmf.inc.underlayTip")}
                            display={r?.underlay_m2 != null ? (r.underlay_m2 === 0 ? t("pmf.inc.attached") : money(r.underlay_m2)) : <span className="text-warning">{t("pmf.set")}</span>}
                            initial={r?.underlay_m2 != null ? String(r.underlay_m2) : ""} onSave={(raw) => { const v = amount(raw); if (v !== undefined) void save(x.supplier, x.category, { underlay_m2: v }); }} />
                        : <span className="text-micro text-muted-foreground">{t("pmf.inc.glued")}</span>}
                    </td>
                    <td className={cn(CELL, "text-right")}>
                      <EditCell label={t("pmf.inc.scotiaFor", { name: `${x.supplier} ${x.category}` })} disabled={busy}
                        display={r?.scotia_lm != null ? money(r.scotia_lm) : <span className="text-warning">{t("pmf.set")}</span>}
                        initial={r?.scotia_lm != null ? String(r.scotia_lm) : ""} onSave={(raw) => { const v = amount(raw); if (v !== undefined) void save(x.supplier, x.category, { scotia_lm: v }); }} />
                    </td>
                    <td className={cn(CELL, "text-micro text-muted-foreground")}>{r?.note ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-4 py-2 text-micro text-muted-foreground">{t("pmf.inc.help")}</p>
        </div>
      )}
    </Panel>
  );
}

/** The engine's reasons, in the reader's language when they are the known ones; anything else as written. */
const issueText = (issue: string, t: T) => (/material cost/i.test(issue) ? t("pmf.x.issue.material") : /LM cost/i.test(issue) ? t("pmf.x.issue.carpet") : /roll width/i.test(issue) ? t("pmf.x.issue.width") : issue);

/** How the cost is worked out (25 Oct 2026) — the shared @aiw/pricing breakdown, loaded when the row opens. */
function CostBreakdown({ p, t }: { p: PriceMyFloorRow; t: T }) {
  const q = usePriceMyFloorCost(p.id);
  if (q.isLoading) return <p className="text-sm text-muted-foreground">{t("pmf.x.loading")}</p>;
  if (q.error || !q.data) return <p className="text-sm text-warning">{(q.error as Error | null)?.message ?? "—"}</p>;
  const c = q.data;
  const sub = "border border-border px-2 py-1.5";
  const label = cn(sub, "text-muted-foreground");
  const parts = c.install ? t("pmf.x.installParts", {
    labour: c.install.labour != null ? money(c.install.labour) : "—",
    glue: c.install.glue != null ? t("pmf.x.glue", { amount: money(c.install.glue) }) : "",
    underlay: c.install.underlay != null ? t("pmf.x.underlay", { amount: money(c.install.underlay) }) : "",
    unit: c.install.unit, width: c.install.unit === "LM" && c.roll_width ? t("pmf.x.perWidth", { width: c.roll_width }) : "",
  }) : "";
  return (
    <div className="space-y-3 text-sm" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium">{t("pmf.x.title")}</h3>
        <Button size="sm" variant="outline" onClick={() => askEmma(t("pmf.x.briefPrompt", { name: p.customerName, id: p.id }))}>
          <MessageSquareText className="mr-1 h-4 w-4" aria-hidden />{t("pmf.x.brief")}
        </Button>
      </div>
      {c.fallback && (
        <p className="rounded-md bg-warning/15 px-3 py-2 text-warning">
          {t("pmf.x.fallback", { issues: c.issues.map((i) => issueText(i, t)).join("; "), cost: money(c.fallback.cost), row: c.fallback.row,
            unit: c.fallback.unit ? t("pmf.x.fallbackUnit", { unit: c.fallback.unit }) : t("pmf.x.fallbackNoUnit") })}
        </p>
      )}
      <table className="w-full max-w-[860px] table-fixed border-collapse">
        <colgroup><col style={{ width: "34%" }} /><col style={{ width: 120 }} /><col style={{ width: 96 }} /><col /></colgroup>
        <thead className="text-left font-mono text-micro uppercase text-muted-foreground">
          <tr><th className={sub}>{t("pmf.x.colour")}</th><th className={cn(sub, "text-right")}>{t("pmf.x.cost")}</th><th className={cn(sub, "text-right")}>{t("pmf.x.perM2")}</th><th className={sub}>{t("pmf.x.source")}</th></tr>
        </thead>
        <tbody>
          {c.colours.length === 0 && <tr><td colSpan={4} className={label}>{t("pmf.x.noColours")}</td></tr>}
          {c.colours.map((x) => (
            <tr key={x.variant_id} className={cn(x.dearest && "bg-primary/5")}>
              <td className={sub}>{x.name}{x.dearest && <span className="ml-2 text-micro text-primary">{t("pmf.x.dearest")}</span>}</td>
              <td className={cn(sub, "num text-right")}>{x.cost != null ? t("pmf.x.perUnit", { amount: money(x.cost), unit: x.unit }) : "—"}</td>
              <td className={cn(sub, "num text-right")}>{x.per_m2 != null ? money(x.per_m2) : "—"}</td>
              <td className={cn(sub, x.source === "none" ? "text-warning" : "text-muted-foreground")}>{t(`pmf.x.src.${x.source}` as MessageId, { date: x.effective_date ?? "" })}</td>
            </tr>
          ))}
          {c.roll_width != null && <tr><td colSpan={4} className={label}>{t("pmf.x.width", { width: c.roll_width })}</td></tr>}
          <tr>
            <td className={label}>{c.install ? t("pmf.x.install", { name: c.install.name }) : t("pmf.col.install")}</td>
            <td className={cn(sub, "num text-right")} colSpan={2}>{c.install?.cost_m2 != null ? money(c.install.cost_m2) : "—"}</td><td className={label}>{c.install?.cost_m2 != null ? parts : t("pmf.x.noInstall")}</td>
          </tr>
          {c.included && (
            <tr>
              <td className={label}>{t("pmf.x.included")}</td>
              <td className={cn(sub, "num text-right", c.included.cost_m2 == null && "text-warning")} colSpan={2}>{c.included.cost_m2 != null ? money(c.included.cost_m2) : t("pmf.inc.missing")}</td>
              <td className={label}>{t("pmf.x.includedHow", {
                underlay: c.included.underlay_m2 == null ? t("pmf.inc.unknown") : c.included.underlay_m2 === 0 ? t("pmf.inc.none") : money(c.included.underlay_m2),
                scotia: c.included.scotia_m2 == null ? t("pmf.inc.unknown") : money(c.included.scotia_m2) })}</td>
            </tr>
          )}
          {c.basis === "supply" && c.methods.map((m) => (
            <tr key={m.id}>
              <td className={label}>{methodLabel(m.id, t)} · {m.name}</td>
              <td className={cn(sub, "num text-right")} colSpan={2}>{m.price != null ? money(m.price) : "—"}</td>
              <td className={label}>{m.price != null && m.cost_m2 != null ? t("pmf.x.methodHow", { cost: money(m.cost_m2), gm: m.gm_pct ?? "—" }) : m.missing.join(", ")}</td>
            </tr>
          ))}
          <tr className="font-medium">
            <td className={sub}>{t("pmf.x.total")}</td>
            <td className={cn(sub, "num text-right")} colSpan={2}>{c.total_cost_m2 != null ? money(c.total_cost_m2) : "—"}</td>
            <td className={cn(sub, "font-normal text-muted-foreground")}>{t("pmf.x.totalHow.installed")}{c.basis === "supply" && c.methods[0] ? ` · ${methodLabel(c.methods[0].id, t)}` : ""}</td>
          </tr>
          <tr className="font-medium">
            <td className={sub}>{t("pmf.x.gm")}</td>
            <td className={cn(sub, "num text-right", c.gm_pct != null && c.gm_pct <= 0 && "text-warning")} colSpan={2}>{c.gm_pct != null ? `${c.gm_pct}%` : "—"}</td>
            <td className={cn(sub, "font-normal text-muted-foreground")}>{c.methods[0]?.price != null && c.total_cost_m2 != null ? t("pmf.x.gmHow", { price: money(c.methods[0].price), cost: money(c.total_cost_m2) }) : ""}</td>
          </tr>
        </tbody>
      </table>
      {c.listing_check_differs && c.listing_check_cost != null && c.supplier_cost_m2 != null && (
        <p className="text-warning">{t("pmf.x.check", { cost: money(c.listing_check_cost), page: money(c.supplier_cost_m2) })}</p>
      )}
      <p className="text-micro text-muted-foreground">
        {t("pmf.x.notIncluded")}: {[t("pmf.x.ni.waste"), t("pmf.x.ni.accessories"), t("pmf.x.ni.prep")].join(" · ")}
        {p.changedAt && <> · {t("pmf.x.changed", { day: day(p.changedAt) })}</>}
      </p>
    </div>
  );
}
